import { getCoreRowModel, useReactTable, Row } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Table } from "shared/app/Table";
import {
  EnrollmentCase,
  RentReportingData
} from "views/rent-reporting-id/types";
import { EnrollmentTabs } from "./components/EnrollmentTabs";
import { MobileRentRow } from "./components/MobileRentRow";
import { useRentalVerifications } from "./hooks/useRentalVerifications";
import { useTableColumns } from "./hooks/useTableColumns";

interface RentReportingTableProps {
  enrollmentCases?: EnrollmentCase[];
}

export const RentReportingTable = ({
  enrollmentCases = []
}: RentReportingTableProps) => {
  const [activeTab, setActiveTab] = useState<string>("");

  const tabIds = useMemo(
    () => enrollmentCases.map((c) => String(c.id)),
    [enrollmentCases]
  );

  useEffect(() => {
    if (tabIds.length > 0 && !tabIds.includes(activeTab))
      setActiveTab(tabIds[0]);
  }, [tabIds]);

  const selectedEnrollmentCase = useMemo(() => {
    return enrollmentCases.find((c) => String(c.id) === activeTab);
  }, [enrollmentCases, activeTab]);

  const { data: rentalVerifications } = useRentalVerifications(activeTab);

  // Need this to prevent infinite render on useReactTable
  const activeCaseData = useMemo(
    () => rentalVerifications || [],
    [rentalVerifications]
  );

  const columns = useTableColumns(selectedEnrollmentCase);

  const table = useReactTable({
    data: activeCaseData,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  const mobileRowRenderer = useCallback(
    (row: Row<RentReportingData>) => (
      <MobileRentRow
        row={row}
        selectedEnrollmentCase={selectedEnrollmentCase}
      />
    ),
    [selectedEnrollmentCase]
  );

  return (
    <div className="mt-6">
      <EnrollmentTabs
        enrollmentCases={enrollmentCases}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <Table
        table={table}
        emptyStateTitle="No rent for reporting yet"
        mobileRowClassName="max-w-[calc(100vw-1.5rem)]"
        mobileRow={mobileRowRenderer}
      />
    </div>
  );
};
